import React, { Component } from 'react';
import isArray from 'lodash/isArray';
import sortBy from 'lodash/sortBy';
import Modal from '../modal';
import ShipstationModule from '../shipstation';
import UI from '../ui';
import Utils from '../utils';
import Editor from './Editor.component';
import Header from './Header.component';
import Viewport from './Viewport.component';
import Util from './Util';
import MessageModalWithCheckbox from './MessageModalWithCheckbox.component';
import AccountVerificationModal from '../accountVerification/AccountVerificationModal.component';

const {
	Session,
	withLayoutAndModalContexts,
	Communication,
	Validation,
	Logger: { logToServer },
} = Utils;
const { ModalButtonActionType } = Modal;
const { ShipStation } = ShipstationModule;
const { Spinner } = UI;

class BrandedTracking extends Component {
	constructor(props) {
		super(props);
		this.modalContext = props.modalContext;
		this.canUseBrandedEmails = Util.canUseBrandedEmails();
		this.state = {
			activeTabIndex: 0,
			branding: {
				brandingId: null,
				brandingProperties: {},
				reference: {},
			},
			notification: {
				notificationSettingId: null,
				notificationSettingProperties: {
					AllowSMSOptIn: false,
					Triggers: [{ NotificationType: '', Channels: [{ ChannelType: '' }] }],
					EmailConfig: {
						ContentBlocks: [{ ContentBlockType: '', Value: '' }],
						FromName: '',
						ReplyToEmail: '',
						BCCEmails: [],
					},
				},
				reference: {},
			},
			bccEmailsEnabled: false,
			storesList: [],
			loading: false,
			isValidationNeeded: false,
		};
	}

	async componentDidMount() {
		// make a request to get user's ShipStation lookup object
		const lookup = await ShipStation.getLookup();

		const branding = await this.getBranding(lookup.Stores);

		const {
			brandingProperties: { BrandName: fromName },
		} = branding;

		const notification = this.canUseBrandedEmails
			? await this.getNotification({ fromName })
			: Util.getNotificationSettingsDataDefault({ fromName });

		// if branded tracking is setup and branded email as allowed, navigate to branded email at once
		const initialActiveTabIndex = this.canUseBrandedEmails && branding.brandingId ? 3 : 0;

		this.setState({
			branding,
			notification,
			activeTabIndex: initialActiveTabIndex,
			// create a new array of stores ordered by "Manual Orders" + whatever stores user has
			storesList: isArray(lookup.Stores)
				? sortBy(lookup.Stores, store => store.MarketplaceID)
				: [],
		});
	}

	getBranding = async lookupStores => {
		const { showError } = this.modalContext;

		const response = await Communication.getBrandedTracking();

		if (!Communication.isSuccessful(response)) {
			return showError({ response });
		}

		const branding = Util.getBrandedTrackingData(response);

		if (isArray(lookupStores)) {
			branding.reference.enabledPrints.stores = lookupStores.map(
				lookupStore =>
					branding.reference.enabledPrints.stores.find(
						m => m.storeId === lookupStore.StoreID.toString()
					) || {
						storeId: lookupStore.StoreID.toString(),
						included:
							branding.brandingId !== null
								? false
								: !Util.isStoreDisabled(lookupStore),
					}
			);
		}

		return branding;
	};

	getNotification = async ({ fromName }) => {
		const { showError } = this.modalContext;

		const notificationResponse = await Communication.getNotificationSetting();

		if (!Communication.isSuccessful(notificationResponse)) {
			return showError({ notificationResponse });
		}

		const notificationEmailResponse = await Communication.getShipNotificationEmail();

		if (!Communication.isSuccessful(notificationEmailResponse)) {
			return showError({ notificationEmailResponse });
		}

		const {
			data: { SenderName = '', PromotionalFooter = '' } = {},
		} = notificationEmailResponse;

		const { customerDetails = {} } = Session.getAccountInfo();
		const customerEmail = customerDetails?.email;

		const notification = Util.getNotificationSettingsData({
			...notificationResponse,
			fromName: fromName || SenderName || '',
			promotional: PromotionalFooter || '',
			customerEmail,
		});

		return notification;
	};

	setReference = newReference => {
		this.setState(({ branding, notification }) => ({
			branding: {
				...branding,
				reference: {
					...newReference,
				},
			},
			notification: {
				...notification,
				reference: {
					...newReference,
				},
			},
		}));
	};

	setBrandedTrackingConfig = newBrandingProperties => {
		this.setState(({ branding, notification }) => ({
			branding: {
				...branding,
				brandingProperties: {
					...branding.brandingProperties,
					...newBrandingProperties,
				},
			},
			notification: {
				...notification,
			},
		}));
	};

	setNotificationSettingsConfig = newNotificationSettingProperties => {
		const { branding, notification } = { ...this.state };

		const [path] = Object.keys(newNotificationSettingProperties);
		const value = newNotificationSettingProperties[path];

		// get object that owns the target field
		const object = path
			.split('.')
			.slice(0, -1)
			.reduce(
				(obj, part) => (obj === undefined ? undefined : obj[part]),
				notification.notificationSettingProperties
			);

		if (object !== undefined) {
			// get field name (last item in the path)
			const [name] = path.split('.').slice(-1);

			// update value of the field by its name
			object[name] = value;
		}

		this.setState({
			branding: {
				...branding,
			},
			notification: {
				...notification,
				notificationSettingProperties: {
					...notification.notificationSettingProperties,
				},
			},
		});
	};

	setBccEmailsEnabled = bccEmailsEnabled => {
		this.setState({ bccEmailsEnabled });
	};

	// Validates all tabs if action, otherwize a specifed tab only
	isUserInputValid = action => {
		const {
			branding: {
				brandingProperties: {
					BrandName,
					LogoType,
					LogoUrl,
					LogoTargetUrl,
					MenuLinks,
					Email,
					Phone,
				},
			},
			notification: {
				notificationSettingProperties: {
					EmailConfig: { FromName, ReplyToEmail, BCCEmails },
				},
			},
			bccEmailsEnabled,
			activeTabIndex,
		} = this.state;

		const isDesignTabValidationNeeded = activeTabIndex === 0 || !!action;
		const isDesignTabValid = isDesignTabValidationNeeded
			? !(
					!BrandName ||
					Util.isLogoTargetUrlInvalid(LogoTargetUrl) ||
					Util.isLogoInvalid(LogoType, LogoUrl)
			  )
			: true;

		const isLinksTabValidationNeeded = activeTabIndex === 1 || !!action;
		const isEmailValid = Email ? Validation.isValidEmail(Email) : true;
		const isPhoneValid = Phone ? Validation.isValidPhone(Phone) : true;

		const isLinksTabValid = isLinksTabValidationNeeded
			? !Util.isMenuLinksInvalid(MenuLinks) && isPhoneValid && isEmailValid
			: true;

		const isEmailsTabValidationNeeded =
			this.canUseBrandedEmails && (activeTabIndex === 3 || !!action);
		const isEmailsTabValid = isEmailsTabValidationNeeded
			? !(
					!FromName ||
					!Validation.isValidEmail(ReplyToEmail) ||
					(bccEmailsEnabled &&
						(!isArray(BCCEmails) ||
							BCCEmails.length === 0 ||
							BCCEmails.some(email => !Validation.isValidEmail(email.EmailAddress))))
			  )
			: true;

		return isDesignTabValid && isLinksTabValid && isEmailsTabValid;
	};

	setActiveTabIndex = activeTabIndex => {
		if (this.isUserInputValid()) {
			this.setState({ activeTabIndex, isValidationNeeded: false });
		} else {
			this.setState({ isValidationNeeded: true });
		}
	};

	onNext = () => {
		const { activeTabIndex } = this.state;

		this.setActiveTabIndex(activeTabIndex + 1);
	};

	onSave = async () => {
		const action = 'save';

		if (this.isUserInputValid(action)) {
			await this.createOrModifyBrandedTracking(action);
		} else {
			this.setState({ isValidationNeeded: true });
		}
	};

	onPublish = async () => {
		const {
			modalContext: { showMessage },
		} = this.props;
		const action = 'publish';

		if (this.canUseBrandedEmails) {
			showMessage({
				data: {
					title: 'Confirm Branding Changes',
					description:
						'Click "Continue" to publish these changes to your Branded Tracking Page and Branded Emails.',
					primaryButtonType: ModalButtonActionType.Confirm,
					primaryButtonText: 'Continue',
					secondaryButtonType: ModalButtonActionType.Cancel,
					secondaryButtonText: 'Cancel',
				},
				closeCallback: async closeType => {
					if (closeType === ModalButtonActionType.Confirm) {
						await this.createOrModifyBrandedTracking(action);
					}
				},
			});
		} else {
			await this.createOrModifyBrandedTracking(action);
		}
	};

	verifyAccountAndPublish = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		const action = 'publish';

		if (this.isUserInputValid(action)) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.onPublish,
				},
			});
		} else {
			this.setState({ isValidationNeeded: true });
		}
	};

	createOrModifyBrandedTracking = async action => {
		const { showMessage, showModal, showError } = this.modalContext;
		const {
			branding: { brandingId, brandingProperties, reference },
			notification: { notificationSettingId, notificationSettingProperties },
			bccEmailsEnabled,
			storesList,
		} = this.state;

		// do not update the state with isActive until the POST\PUT request completed sucessfully
		const ref = action === 'publish' ? { ...reference, isActive: true } : { ...reference };

		// show spinner
		this.setState({ loading: true });

		const brandingResponse = brandingId
			? await Communication.modifyBrandedTracking({
					Branding: {
						BrandingId: brandingId,
						BrandingProperties: Util.getBrandingProperties(brandingProperties),
						Reference: JSON.stringify(ref),
					},
					SetAsDefault: false,
			  })
			: await Communication.createBrandedTracking({
					Properties: Util.getBrandingProperties(brandingProperties),
					Reference: JSON.stringify(ref),
					SetAsDefault: false,
			  });

		let notificationResponse = null;

		if (this.canUseBrandedEmails) {
			notificationResponse = notificationSettingId
				? await Communication.modifyNotificationSetting({
						NotificationSetting: {
							NotificationSettingId: notificationSettingId,
							NotificationSettingProperties: Util.getNotificationSettingProperties(
								notificationSettingProperties
							),
							Reference: JSON.stringify(ref),
						},
						SetAsDefault: true,
				  })
				: await Communication.createNotificationSetting({
						Properties: Util.getNotificationSettingProperties(
							notificationSettingProperties
						),
						Reference: JSON.stringify(ref),
						SetAsDefault: true,
				  });
		}

		if (
			Communication.isSuccessful(brandingResponse) &&
			(this.canUseBrandedEmails ? Communication.isSuccessful(notificationResponse) : true)
		) {
			this.setState({
				isValidationNeeded: false,
				branding: {
					brandingProperties: {
						...brandingProperties,
						TempLogoId: '',
					},
					brandingId: brandingResponse.data.brandingId || brandingId,
					reference: { ...ref },
				},
				notification: {
					notificationSettingProperties: { ...notificationSettingProperties },
					notificationSettingId: notificationResponse
						? notificationResponse.data.notificationSettingId || notificationSettingId
						: null,
					reference: { ...ref },
				},
				// hide spinner
				loading: false,
			});

			const skipBrandIsLiveModal = localStorage.getItem('skipBrandIsLiveModal');

			if (action === 'publish') {
				if (this.canUseBrandedEmails) {
					if (skipBrandIsLiveModal !== 'true') {
						showModal({
							component: MessageModalWithCheckbox,
							data: {
								title: 'Your Brand Is Live',
								description:
									'Your Branded Tracking Page and Branded Email are now live.',
								checkboxLabel: "Don't show this again.",
								checkboxValue: false,
								closeCallback: async ({ actionType, checkboxValue }) => {
									if (actionType === ModalButtonActionType.Ok) {
										localStorage.setItem('skipBrandIsLiveModal', checkboxValue);
									}
								},
								dataqa: 'branded-tracking-dont-show-brand-live',
							},
						});
					}
				} else {
					showMessage({
						data: {
							title: "You're All Set",
							description:
								'Your Branded Tracking page and settings changes are now live. This page can be accessed by clicking on the tracking number in your shipment notification emails.',
							primaryButtonType: ModalButtonActionType.Ok,
							primaryButtonText: 'Got It',
						},
					});
				}
			} else if (action === 'save') {
				showMessage({
					data: {
						title: 'Changes Saved',
						description:
							'Your Branded Tracking page settings changes have been saved. These changes will not be published until you click "Publish Now".',
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Got It',
					},
				});
			}

			logToServer(
				Util.composeLogObject(
					brandingProperties,
					notificationSettingProperties,
					bccEmailsEnabled,
					storesList,
					reference,
					action
				)
			);
		} else {
			// hide spinner
			this.setState({ loading: false });

			showError({
				brandingResponse,
				notificationResponse,
			});
		}
	};

	render() {
		const {
			storesList,
			branding,
			notification,
			activeTabIndex,
			bccEmailsEnabled,
			loading,
			isValidationNeeded,
		} = this.state;

		return (
			<div className="common-branded-tracking">
				<Header />
				<div className="preview-and-editor-wrapper">
					<Viewport
						brandedTrackingConfig={branding.brandingProperties}
						notificationConfig={notification.notificationSettingProperties}
					/>
					<Editor
						branding={branding}
						notification={notification}
						storesList={storesList}
						activeTabIndex={activeTabIndex}
						bccEmailsEnabled={bccEmailsEnabled}
						isValidationNeeded={isValidationNeeded}
						setReference={this.setReference}
						setBrandedTrackingConfig={this.setBrandedTrackingConfig}
						setNotificationSettingsConfig={this.setNotificationSettingsConfig}
						setActiveTabIndex={this.setActiveTabIndex}
						setBccEmailsEnabled={this.setBccEmailsEnabled}
						onNext={this.onNext}
						onSave={this.onSave}
						onPublish={this.verifyAccountAndPublish}
					/>
				</div>
				{loading && <Spinner msg="Processing..." />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(BrandedTracking);
